'use client';
import { Box, Link } from '@mui/material';

import { CloseIcon, InfoPlusIcon, useTranslation } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import {
  AccountActivationState,
  TOTAL_STEPS_WITH_SETTINGS,
} from '../../../../organisms/AccountActivation/account-activation.constants';
import { userStoreSelector } from '../../../../store';

import {
  barStyles,
  closeIconStyles,
  containerStyles,
  contentStyles,
  ctaStyles,
  headerStyles,
  infoIconStyles,
  progressStyles,
  progressTextStyles,
  titleStyles,
} from './InfoStepper.styling';
import { ProgressBarItem } from './ProgressBarItem';
import { InfoStepperProps } from './types';
import { calculateCompletedSteps } from './utils';

const InfoStepper = ({
  i18n,
  totalSteps: totalStepsProperty,
  showNudge,
}: InfoStepperProps) => {
  const { navigationLinks } = usePageContext();
  const isEmailVerified = userStoreSelector.use.isEmailVerified();
  const setCancelNudge = userStoreSelector.use.setCancelNudge();
  const activationState = userStoreSelector.use.activationState();
  const isAuthenticated = userStoreSelector.use.isAuthenticated();
  const isCancelNudge = userStoreSelector.use.isCancelNudge();
  const t = useTranslation(i18n);

  const totalSteps = totalStepsProperty
    ? Number(totalStepsProperty)
    : TOTAL_STEPS_WITH_SETTINGS;

  const isVisible =
    isAuthenticated && showNudge && !isCancelNudge && !!activationState;

  if (!isVisible) {
    return null;
  }

  const isAccountActivated =
    isEmailVerified &&
    !!activationState?.includes?.(AccountActivationState.ACTIVATED);

  if (isAccountActivated) {
    return null;
  }

  const completedSteps = calculateCompletedSteps(
    activationState,
    isEmailVerified,
    totalSteps,
  );

  const title = i18n['header.accountMenu.nudge.title'];
  const description = i18n['header.accountMenu.nudge.description'];
  const steps = t('header.accountMenu.completedSteps.label', {
    steps: completedSteps,
    totalSteps,
  });
  const ctaMessage = i18n['header.accountMenu.completeProfile.label'];

  const handleClose = () => setCancelNudge(true);

  const shouldShowProgress = Boolean(steps && totalSteps > 0);

  const ctaLink = isEmailVerified
    ? navigationLinks?.accountDashboard
    : navigationLinks?.accountActivation;

  const shouldShowCta = Boolean(ctaMessage && ctaLink);

  const renderProgressBarItems = () =>
    Array.from({ length: totalSteps }, (_, index) => (
      <ProgressBarItem
        key={index}
        index={index}
        isCompleted={index < completedSteps}
      />
    ));

  return (
    <Box
      sx={containerStyles}
      role="alert"
      aria-live="polite"
      data-testid="info-stepper"
    >
      <Box sx={headerStyles}>
        <InfoPlusIcon
          sx={infoIconStyles}
          aria-hidden="true"
          data-testid="info-icon"
        />
        <Box sx={titleStyles} component="h3" data-testid="info-title">
          {title}
        </Box>
        <CloseIcon
          sx={closeIconStyles}
          onClick={handleClose}
          role="button"
          tabIndex={0}
          aria-label="Close alert"
          data-testid="close-icon"
        />
      </Box>
      <Box sx={contentStyles}>
        {description && (
          <Box sx={progressTextStyles} data-testid="info-description">
            {description}
          </Box>
        )}
        {shouldShowProgress && (
          <Box sx={progressStyles}>
            <Box sx={progressTextStyles} data-testid="progress-text">
              {steps}
            </Box>
            <Box
              sx={barStyles}
              role="progressbar"
              aria-valuenow={completedSteps}
              aria-valuemax={totalSteps}
              aria-valuemin={0}
              aria-hidden="true"
              data-testid="progress-bar"
            >
              {renderProgressBarItems()}
            </Box>
          </Box>
        )}
        {shouldShowCta && (
          <Link
            href={ctaLink}
            sx={ctaStyles}
            aria-label={`Call to action: ${ctaMessage}`}
            data-testid="cta-link"
          >
            {ctaMessage}
          </Link>
        )}
      </Box>
    </Box>
  );
};

export default InfoStepper;
